/**
 * Billing page API functions.
 */

import { getBillingInformation as getBillingInfoBase } from "@/utils/billing/api";
import type { BillingPortalInfo } from "@/utils/billing/types";
import type { BillingActionType } from "./types";
import axios from "axios";
import { backend_url } from "@/constants";
import { AXIOS_CONFIG } from "@/utils/api";
import type {
  CancellationPreview,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  PayByPrepFeesInfo,
  UpdateBankStatusRequest,
} from "./types";

/**
 * Get billing information and handle different action types.
 * @param returnUrl - URL to redirect to after billing portal session
 * @param type - Optional action type (subscription_cancel, subscription_update)
 * @param skipPortal - If true, no Stripe portal/customer session is created
 * @returns Promise resolving to billing portal information
 */
export const getBillingInformation = async (
  returnUrl?: string,
  type?: BillingActionType,
  skipPortal?: boolean
): Promise<BillingPortalInfo> => {
  return getBillingInfoBase(returnUrl, type, skipPortal);
};

/**
 * Load Stripe pricing table script dynamically.
 * Prevents duplicate script loading by checking if script already exists.
 */
export const loadStripePricingTableScript = (): void => {
  if (document.querySelector('script[src*="js.stripe.com/v3/pricing-table"]')) {
    return; // Script already loaded
  }

  const script = document.createElement("script");
  script.src = "https://js.stripe.com/v3/pricing-table.js";
  script.async = true;
  document.head.appendChild(script);
};

/**
 * Preview what will happen if the user cancels their subscription.
 *
 * Returns information about refund eligibility, amount, and effective dates
 * without actually performing the cancellation.
 */
export const previewCancellation = async (): Promise<CancellationPreview> => {
  const response = await axios.get(
    `${backend_url}/api/stripe/management/subscription/cancel/preview/`,
    AXIOS_CONFIG
  );
  return response.data?.content ?? response.data;
};

/**
 * Cancel the user's subscription.
 *
 * If within 14 days of plan start: Cancels immediately with prorated refund.
 * If outside 14 days: Sets subscription to cancel at end of billing period.
 *
 * @param payload - Cancellation reason and optional additional comments
 */
export const cancelSubscription = async (
  payload: CancelSubscriptionRequest
): Promise<CancelSubscriptionResponse> => {
  const response = await axios.post(
    `${backend_url}/api/stripe/management/subscription/cancel/`,
    payload,
    AXIOS_CONFIG
  );
  return response.data?.content ?? response.data;
};

/**
 * Get bank status information for Pay by Prep Fees users.
 *
 * Returns bank product, EFIN, approval status, and whether the bank
 * is supported for Pay by Prep Fees.
 *
 * @returns Promise resolving to bank status information
 */
export const getBankStatus = async (): Promise<PayByPrepFeesInfo> => {
  const response = await axios.get(
    `${backend_url}/api/stripe/management/bank-status/`,
    AXIOS_CONFIG
  );
  const data = response.data?.content ?? response.data;
  // Backend already returns camelCase keys
  return {
    bankProduct: data.bankProduct,
    efin: data.efin,
    approvalStatus: data.approvalStatus,
    approvalMessage: data.approvalMessage,
    isBankSupported: data.isBankSupported,
    lastUpdated: data.lastUpdated,
  };
};

/** Response from POST /api/stripe/management/purchase/ (contract top-up). */
export interface CreditBundlePurchaseResponse {
  requestId: string;
  grantId: string;
  status: string;
  addOnUnits: number;
  unitPriceCents: number;
  committedValueCents: number;
  currentTotalCommittedUnits: number;
  updatedTotalCommittedUnits: number;
  newBusinessBalance?: number;
}

/** Response from GET /api/stripe/management/purchase/status/ (invoice status). Backend returns camelCase. */
export interface CreditBundleInvoiceStatusResponse {
  status: string;
  paymentFailedReason: string | null;
}

export interface CreditBundlePurchasePreviewResponse {
  addOnUnits: number;
  unitPriceCents: number;
  committedValueCents: number;
  currentTotalCommittedUnits: number;
  updatedTotalCommittedUnits: number;
}

export interface CreditPricingTierContext {
  id: number;
  minQuantity: number;
  maxQuantity: number | null;
  unitPriceCents: number;
  internalName: string;
}

export interface CreditBundlePricingContextResponse {
  currentTotalCommittedUnits: number;
  currentCommittedValueCents: number;
  minPurchaseQuantity: number;
  scheduleId: number;
  scheduleName: string;
  tiers: CreditPricingTierContext[];
}

/**
 * Purchase a credit bundle (10, 25, 50, or 100 credits).
 * Creates and charges a Stripe invoice; credits are issued via webhook.
 *
 * @param amount - One of 10, 25, 50, 100
 * @returns Invoice id and status (e.g. "paid")
 */
export const purchaseCreditBundle = async (
  amount: number,
  requestId: string
): Promise<CreditBundlePurchaseResponse> => {
  const response = await axios.post(
    `${backend_url}/api/stripe/management/purchase/`,
    { amount, requestId },
    AXIOS_CONFIG
  );
  return response.data?.content ?? response.data;
};

export const previewCreditBundlePurchase = async (
  amount: number
): Promise<CreditBundlePurchasePreviewResponse> => {
  const response = await axios.get(
    `${backend_url}/api/stripe/management/purchase/`,
    { ...AXIOS_CONFIG, params: { amount } }
  );
  return response.data?.content ?? response.data;
};

export const getCreditBundlePricingContext =
  async (): Promise<CreditBundlePricingContextResponse> => {
    const response = await axios.get(
      `${backend_url}/api/stripe/management/purchase/pricing-context/`,
      AXIOS_CONFIG
    );
    return response.data?.content ?? response.data;
  };

/**
 * Get invoice status for credit bundle purchase (source of truth for outcome).
 * Used to poll until status is "paid" or a terminal failure.
 *
 * @param invoiceId - Stripe invoice ID (e.g. in_xxx)
 * @returns status and optional payment_failed_reason
 */
export const getCreditBundleInvoiceStatus = async (
  invoiceId: string
): Promise<CreditBundleInvoiceStatusResponse> => {
  const response = await axios.get(
    `${backend_url}/api/stripe/management/purchase/status/`,
    { ...AXIOS_CONFIG, params: { invoice_id: invoiceId } }
  );
  const data = response.data?.content ?? response.data;
  return {
    status: data.status,
    paymentFailedReason:
      data.paymentFailedReason ?? data.payment_failed_reason ?? null,
  };
};

/** Active commercial add-on line from GET .../commercial-add-ons/catalog/ */
/** Matches ``AddOnCatalog.catalog_key`` for the TaxSlayer commercial add-on. */
export const COMMERCIAL_ADD_ON_CATALOG_KEY_TAXSLAYER = "taxslayer";

export interface CommercialAddOnOffering {
  id: string;
  priceId: string;
  catalogKey: string;
  displayTitle: string;
  displayDescription: string;
  /** HTTPS URL for modal artwork; empty uses default icon. */
  displayImageUrl?: string;
  /** True when this catalog row is already a paid line on the active contract. */
  alreadyPurchased?: boolean;
  amountCents: number;
  currency: string;
}

/**
 * List staff-enabled commercial add-ons (one-time prices) for contract billing UI.
 */
export const getCommercialAddOnCatalog = async (): Promise<{
  offerings: CommercialAddOnOffering[];
}> => {
  const response = await axios.get(
    `${backend_url}/api/stripe/management/commercial-add-ons/catalog/`,
    AXIOS_CONFIG
  );
  return response.data?.content ?? response.data;
};

export interface CommercialAddOnPurchaseResponse {
  invoiceId: string;
  status: string;
  amountPaid: number | null;
}

/**
 * Charge a one-time add-on on the subscription invoice (card on file).
 */
export const purchaseCommercialAddOn = async (
  offeringId: string,
  requestId: string
): Promise<CommercialAddOnPurchaseResponse> => {
  const response = await axios.post(
    `${backend_url}/api/stripe/management/commercial-add-ons/purchase/`,
    { offeringId, requestId },
    AXIOS_CONFIG
  );
  return response.data?.content ?? response.data;
};

/**
 * Update bank status information for Pay by Prep Fees users.
 *
 * When bank product or EFIN changes, the approval status is reset to 'pending'.
 *
 * @param payload - Object with optional efin and bankProduct fields
 * @returns Promise resolving to updated bank status information
 */
export const updateBankStatus = async (
  payload: UpdateBankStatusRequest
): Promise<PayByPrepFeesInfo> => {
  // Transform camelCase to snake_case for backend
  const backendPayload: Record<string, string> = {};
  if (payload.efin) backendPayload.efin = payload.efin;
  if (payload.bankProduct) backendPayload.bank_product = payload.bankProduct;

  const response = await axios.patch(
    `${backend_url}/api/stripe/management/bank-status/`,
    backendPayload,
    AXIOS_CONFIG
  );
  const data = response.data?.content ?? response.data;
  // Backend already returns camelCase keys
  return {
    bankProduct: data.bankProduct,
    efin: data.efin,
    approvalStatus: data.approvalStatus,
    approvalMessage: data.approvalMessage,
    isBankSupported: data.isBankSupported,
    lastUpdated: data.lastUpdated,
  };
};
